import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';
import getBaseURL from '@utils/api/getBaseURL';

import type { BasketViewModel } from '../models/BasketViewModel';
import type { EmptyResponseViewModel } from '../models/EmptyResponseViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class BasketsService {
  /**
   * Get
   * Get basket
   * @returns BasketViewModel Success
   * @throws ApiError
   */
  public static async getBasket(
    {
      basketId,
      isArchived,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Is archived
       */
      isArchived?: boolean;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<BasketViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<BasketViewModel>({
      method: 'GET',
      url: getBaseURL(`/v2/baskets`, `BasketsService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(isArchived ? { isArchived: isArchived } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Validate Promo Code
   * Validates basket promo code
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async validatePromocode(
    {
      basketId,
      code,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Promo code
       */
      code: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PATCH',
      url: getBaseURL(`/v2/baskets/promocode/validate`, `BasketsService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(code ? { code: code } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Cancel Promo Code
   * Cancels promo code
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async cancelPromoCode(
    {
      basketId,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PATCH',
      url: getBaseURL(`/v2/baskets/promocode/cancel`, `BasketsService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Remove Item
   * Removes an item by basket and item identifier
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async remove(
    {
      basketId,
      itemId,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
      /**
       * Item Id
       */
      itemId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'DELETE',
      url: getBaseURL(`/v2/baskets/remove`, `BasketsService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(itemId ? { itemId: itemId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Unlock Basket
   * Unlocks current basket
   * @returns EmptyResponseViewModel Success
   * @throws ApiError
   */
  public static async unlock(
    {
      basketId,
    }: {
      /**
       * Basket Id
       */
      basketId: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<EmptyResponseViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<EmptyResponseViewModel>({
      method: 'PATCH',
      url: getBaseURL(`/v2/baskets/unlock`, `BasketsService`),
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}
